import { Button } from '@/components/ui/Button'
import { Icon } from '@/components/ui/Icon'
import { Loader } from '@/components/ui/Loader'
import { formatDisplayDate } from '@/lib/date'
import { resolveImageUrl } from '@/lib/image-url'
import { useAuthStore } from '@/store/auth'
import { useRouteStore } from '@/store/route'
import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './Home.module.scss'

export const HomePage = () => {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const charity = useRouteStore((state) => state.charity)
  const loading = useRouteStore((state) => state.loading)
  const donationsLoading = useRouteStore((state) => state.donationsLoading)

  const firstName = user?.name?.split(' ')[0] || 'Driver'
  const selectedVehicleId = useRouteStore((state) => state.selectedVehicleId)
  const vehicles = useRouteStore((state) => state.vehicles)
  const selectedDate = useRouteStore((state) => state.selectedDate)
  const donations = useRouteStore((state) => state.donations)
  const fetchDonations = useRouteStore((state) => state.fetchDonations)

  useEffect(() => {
    fetchDonations()
  }, [selectedVehicleId, selectedDate, fetchDonations])

  const amJobs = useMemo(
    () => donations.filter((d) => d.part_of_day === 'am').length,
    [donations]
  )
  const pmJobs = useMemo(
    () => donations.filter((d) => d.part_of_day === 'pm').length,
    [donations]
  )

  return (
    <div className={styles.container} data-testid="home-page">
      <div className={styles.content}>
        <header className={styles.header} data-testid="home-header">
          {charity?.logo ? (
            <img
              src={resolveImageUrl(charity.logo)}
              alt={charity.name}
              className={styles.logo}
              data-testid="home-charity-logo"
            />
          ) : (
            <div className={styles.logoPlaceholder}></div>
          )}
          <div className={styles.headerInfo}>
            <p className={styles.charityName} data-testid="home-charity-name">
              {charity?.name || ''}
            </p>
            <p className={styles.userName} data-testid="home-user-name">
              {user?.name || ''}
            </p>
          </div>
          <Button
            icon={<Icon name="menu" size={20} className={styles.menuIcon} />}
            onClick={() => navigate('/settings')}
            className={styles.menuButton}
            testId="home-menu-button"
          />
        </header>

        <h1 className={styles.greeting} data-testid="home-greeting">
          Hello {firstName}!
        </h1>

        <div className={styles.infoBar} data-testid="home-info-bar">
          <div className={styles.infoItem}>
            <p className={styles.infoLabel}>Truck:</p>
            <p className={styles.infoValue} data-testid="home-truck-name">
              {vehicles?.find((v) => v.id === selectedVehicleId)?.name || '/'}
            </p>
          </div>
          <div className={styles.infoItem}>
            <p className={styles.infoLabel}>Date:</p>
            <p className={styles.infoValue} data-testid="home-selected-date">
              {selectedDate ? formatDisplayDate(selectedDate) : '/'}
            </p>
          </div>
          <div>
            <Button
              text="Change"
              variant="default"
              icon={<Icon name="arrow" size={16} />}
              onClick={() => navigate('/settings')}
              testId="home-change-button"
            />
          </div>
        </div>

        {loading || donationsLoading ? (
          <Loader />
        ) : (
          <div className={styles.routes} data-testid="home-routes">
            <div className={styles.routeCard} data-testid="home-am-route-card">
              <div className={styles.routeHeader}>
                <div className={styles.routeInfo}>
                  <h2 className={styles.routeTitle}>AM Route</h2>
                  <p className={styles.routeTime}>8-12 AM</p>
                </div>
                {amJobs > 0 && (
                  <Button
                    text="Open"
                    variant="primary"
                    icon={<Icon name="arrow" size={16} />}
                    onClick={() => navigate('/am')}
                    testId="home-am-route-open"
                  />
                )}
              </div>
              <div className={styles.routeJobs}>
                <Icon name="job" size={16} className={styles.jobsIcon} />
                <span>
                  <span data-testid="home-am-jobs-count">{amJobs}</span> jobs
                </span>
              </div>
            </div>

            <div className={styles.routeCard} data-testid="home-pm-route-card">
              <div className={styles.routeHeader}>
                <div className={styles.routeInfo}>
                  <h2 className={styles.routeTitle}>PM Route</h2>
                  <p className={styles.routeTime}>12-4 PM</p>
                </div>
                {pmJobs > 0 && (
                  <Button
                    text="Open"
                    variant="primary"
                    icon={<Icon name="arrow" size={16} />}
                    onClick={() => navigate('/pm')}
                    testId="home-pm-route-open"
                  />
                )}
              </div>
              <div className={styles.routeJobs}>
                <Icon name="job" size={16} className={styles.jobsIcon} />
                <span>
                  <span data-testid="home-pm-jobs-count">{pmJobs}</span> jobs
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
